import { useState } from 'react';
import { useCustomerList } from '../customers/hooks';
import type { CreateVisitPayload, UpdateVisitPayload } from './types';

interface VisitFormProps {
  defaultValues?: Partial<{
    customerId: string;
    startLocal: string;
    endLocal: string;
    locationAddress: string;
    notes: string;
    mileageMiles: string;
  }>;
  lockCustomer?: boolean;
  submitLabel: string;
  submitting?: boolean;
  onSubmit: (payload: CreateVisitPayload | UpdateVisitPayload & { customerId?: string }) => Promise<void> | void;
  onCancel?: () => void;
  errorMessage?: string | null;
}

/**
 * Single form reused for create and edit. Times are kept in the user's
 * local timezone in the <input type="datetime-local"> buffer, then the
 * submitter converts to UTC ISO before sending. The server always stores
 * UTC — see ADR conventions note on timestamps.
 */
export function VisitForm({
  defaultValues,
  lockCustomer,
  submitLabel,
  submitting,
  onSubmit,
  onCancel,
  errorMessage
}: VisitFormProps) {
  const [customerId, setCustomerId] = useState(defaultValues?.customerId ?? '');
  const [customerFilter, setCustomerFilter] = useState('');
  const [startLocal, setStartLocal] = useState(defaultValues?.startLocal ?? '');
  const [endLocal, setEndLocal] = useState(defaultValues?.endLocal ?? '');
  const [locationAddress, setLocationAddress] = useState(defaultValues?.locationAddress ?? '');
  const [notes, setNotes] = useState(defaultValues?.notes ?? '');
  const [mileage, setMileage] = useState(defaultValues?.mileageMiles ?? '');

  const { data: customers } = useCustomerList({
    search: customerFilter,
    includeInactive: false,
    page: 1,
    pageSize: 25
  });

  const canSubmit =
    (lockCustomer || customerId !== '') &&
    startLocal !== '' &&
    endLocal !== '' &&
    new Date(endLocal) > new Date(startLocal);

  // When start changes, auto-bump end to start+1h if end is empty or has
  // become invalid (end <= start). If the user has deliberately set end past
  // start+1h, leave it alone — they're scheduling a longer visit.
  const onStartChange = (next: string) => {
    setStartLocal(next);
    if (!next) return;
    const startDate = new Date(next);
    if (Number.isNaN(startDate.getTime())) return;

    const endDate = endLocal ? new Date(endLocal) : null;
    if (!endDate || Number.isNaN(endDate.getTime()) || endDate <= startDate) {
      const oneHourLater = new Date(startDate.getTime() + 60 * 60 * 1000);
      setEndLocal(toLocalInput(oneHourLater));
    }
  };

  const submit = async () => {
    const startUtc = new Date(startLocal).toISOString();
    const endUtc = new Date(endLocal).toISOString();
    const milesNum = mileage.trim() === '' ? null : Number(mileage);
    await onSubmit({
      customerId: customerId || defaultValues?.customerId || '',
      startUtc,
      endUtc,
      locationAddress: locationAddress.trim() || null,
      notes: notes.trim() || null,
      mileageMiles: Number.isFinite(milesNum as number) ? (milesNum as number) : null
    });
  };

  return (
    <div className="space-y-4">
      {!lockCustomer && (
        <label className="block">
          <span className="block text-sm font-medium text-slate-700 mb-1">Client</span>
          <input
            type="search"
            placeholder="Search clients…"
            value={customerFilter}
            onChange={(e) => setCustomerFilter(e.target.value)}
            className="w-full border border-slate-300 rounded px-3 py-2 min-h-[44px] mb-2"
          />
          <select
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value)}
            className="w-full border border-slate-300 rounded px-3 py-2 min-h-[44px]"
          >
            <option value="">Pick a customer…</option>
            {customers?.items.map((c) => (
              <option key={c.id} value={c.id}>
                {c.displayName}
              </option>
            ))}
          </select>
        </label>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="block text-sm font-medium text-slate-700 mb-1">Start</span>
          <input
            type="datetime-local"
            value={startLocal}
            onChange={(e) => onStartChange(e.target.value)}
            className="w-full border border-slate-300 rounded px-3 py-2 min-h-[44px]"
          />
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-slate-700 mb-1">End</span>
          <input
            type="datetime-local"
            value={endLocal}
            min={startLocal || undefined}
            onChange={(e) => setEndLocal(e.target.value)}
            className="w-full border border-slate-300 rounded px-3 py-2 min-h-[44px]"
          />
        </label>
      </div>

      <label className="block">
        <span className="block text-sm font-medium text-slate-700 mb-1">Location</span>
        <input
          type="text"
          autoComplete="street-address"
          value={locationAddress}
          onChange={(e) => setLocationAddress(e.target.value)}
          className="w-full border border-slate-300 rounded px-3 py-2 min-h-[44px]"
        />
      </label>

      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="block text-sm font-medium text-slate-700 mb-1">Mileage (miles)</span>
          <input
            type="number"
            inputMode="decimal"
            min={0}
            step="0.1"
            value={mileage}
            onChange={(e) => setMileage(e.target.value)}
            className="w-full border border-slate-300 rounded px-3 py-2 min-h-[44px]"
            placeholder="Optional"
          />
        </label>
      </div>

      <label className="block">
        <span className="block text-sm font-medium text-slate-700 mb-1">Notes</span>
        <textarea
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="w-full border border-slate-300 rounded px-3 py-2"
        />
      </label>

      {errorMessage && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">{errorMessage}</p>
      )}

      <div className="flex items-center gap-3 pt-2">
        <button
          type="button"
          onClick={submit}
          disabled={!canSubmit || submitting}
          className="bg-sky-600 text-white px-4 py-2 rounded hover:bg-sky-700 disabled:opacity-50"
        >
          {submitting ? 'Saving…' : submitLabel}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} className="text-slate-600 hover:text-sky-600 text-sm">
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}

function toLocalInput(date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return (
    date.getFullYear() +
    '-' +
    pad(date.getMonth() + 1) +
    '-' +
    pad(date.getDate()) +
    'T' +
    pad(date.getHours()) +
    ':' +
    pad(date.getMinutes())
  );
}
